import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApolloClient } from "@apollo/client";
import clsx from "clsx";
import Loader from "components/Loader/Loader";
import {
  customColorIndex,
  CustomizedYAxisTickLeftCurrency,
  CustomizedYAxisTickRight,
  getAllKeys,
  MAIN_COLORS,
  selectFormat,
  StandardTooltip,
} from "components/Utils/Utils";
import { AppContext } from "contexts/AppContextProvider";
import { findParentNodeByClass, getXrayTooltipPosition } from "helpers/DOM";
import moment from "moment-timezone";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CSVExport from "components/CSVExport/CSVExport";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getChannelAnalysisData, getMixResolvedTargets } from "./MixRequests";
import { XRayTooltip } from "components/Utils/XrayUtils.js";
import {
  closeAllBarchartTooltips,
  fetchInsights,
  InsightsTickIndicator,
  InsightsWithinTooltip,
  RenderBackground,
  RenderTooltipInsights,
} from "components/Utils/InsightsUtils";

const TargetDropdown = ({ selectedMetric, setSelectedMetric, scenario }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { currentProject } = useContext(AppContext).state;
  const apolloClient = useApolloClient();

  const { data: targetsData, isLoading } = useQuery({
    queryKey: [
      "mixResolvedTargets",
      currentProject?.projectObj?.id,
      scenario?.id,
    ],
    enabled: !!currentProject?.projectObj?.id && !!scenario?.id,

    queryFn: () =>
      getMixResolvedTargets(
        apolloClient,
        localStorage.getItem("defaultTenantID"),
        currentProject.projectObj.id,
        scenario.id
      ),
  });

  const metrics = targetsData || [];

  // Get the display name for the selected metric
  const getDisplayName = () => {
    if (isLoading) return "Loading...";
    if (!metrics.length) return "No targets available";

    const selectedTarget = metrics.find((m) => m.id === selectedMetric);
    return selectedTarget?.channelName || "Select target";
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="dropdown" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="btn btn-link dropdown-toggle-target text-decoration-none scenario d-flex align-items-center"
      >
        <span>{getDisplayName()}</span>
        <i className="fas fa-chevron-down ml-2"></i>
      </button>
      {isOpen && (
        <div className="dropdown-menu show">
          {metrics.map((item) => (
            <div
              key={item.id}
              className={clsx("dropdown-item", {
                active: item.id === selectedMetric,
              })}
              onClick={() => {
                setSelectedMetric(item.id);
                setIsOpen(false);
              }}
            >
              {item.channelName}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Toggle button component for switching between Rolling and Last Year views
const TimeframeToggle = ({ timeframeMode, setTimeframeMode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="dropdown ml-3" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="btn btn-link dropdown-toggle-target text-decoration-none scenario d-flex align-items-center"
      >
        <span>{timeframeMode === "rolling" ? "Rolling" : "Last Year"}</span>
        <i className="fas fa-chevron-down ml-2"></i>
      </button>
      {isOpen && (
        <div className="dropdown-menu show">
          <div
            className={clsx("dropdown-item", {
              active: timeframeMode === "rolling",
            })}
            onClick={() => {
              setTimeframeMode("rolling");
              setIsOpen(false);
            }}
          >
            Rolling
          </div>
          <div
            className={clsx("dropdown-item", {
              active: timeframeMode === "lastYear",
            })}
            onClick={() => {
              setTimeframeMode("lastYear");
              setIsOpen(false);
            }}
          >
            Last Year
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Represents the data related to the chanel analysis rendered as stacked bar chart.
 */
const MixChannelAnalysis = ({
  scenario,
  isUsingOnMix = true,
  channelData = [],
  getExportData,
  selectedMetric,
  setSelectedMetric,
  dateRange,
}) => {
  const { currentProject } = useContext(AppContext).state;
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();
  const chartRef = useRef();

  // Add timeframe toggle state with today's year context
  const [timeframeMode, setTimeframeMode] = useState("rolling");
  // Track the reference year for last year calculations
  const [referenceYear, setReferenceYear] = useState(moment().year());

  const [height, setHeight] = useState(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setHeight(divLegendRef?.current?.clientHeight || 0);
  });

  const [state, setState] = useState({
    insights: undefined,
    tooltip: {
      show: false,
      x: 0,
      y: 0,
    },
  });

  const refNode = useRef();
  const divPosition = useRef(null);
  const divLegendRef = useRef();

  // Create a fixed dateRange based on scenario start and end dates
  const fixedDateRangeRef = useRef(null);

  // Only update the date range ref when scenario changes and has valid dates
  // This ensures the date range is fixed when the component mounts and only updates if scenario changes
  useEffect(() => {
    if (scenario?.startDt && scenario?.endDt) {
      fixedDateRangeRef.current = {
        from: moment(scenario.startDt).format("YYYY-MM-DD"),
        to: moment(scenario.endDt).format("YYYY-MM-DD"),
      };
    }
  }, [scenario?.id, scenario?.startDt, scenario?.endDt]); // Only depend on scenario.id and dates

  // Get the date range from MixDatePicker or fallback to query cache if not provided
  const selectedDateRange = useMemo(
    () =>
      dateRange ||
      queryClient.getQueryData(["mixDateRange"]) || {
        from: "2025-05-05",
        to: "2025-07-13",
      },
    [dateRange, queryClient]
  );

  // Calculate last year's date range based on today's year
  const getLastYearDateRange = () => {
    // Use referenceYear (which is set to today's year when toggling) instead of dynamic calculation
    const lastYear = referenceYear - 1;

    // Return the entire previous year (Jan 1 to Dec 31)
    return {
      from: `${lastYear}-01-01`,
      to: `${lastYear}-12-31`,
    };
  };

  // Use either current date range or last year's date range based on toggle selection
  const effectiveDateRange =
    timeframeMode === "rolling"
      ? fixedDateRangeRef.current
      : getLastYearDateRange();

  const { isLoading, data } = useQuery({
    enabled: !!scenario && !!effectiveDateRange && !!selectedMetric,
    queryKey: [
      "channelAnalysisData",
      {
        scenario,
        selectedMetric,
        dateRange: effectiveDateRange,
        timeframeMode,
      },
    ],
    queryFn: () =>
      getChannelAnalysisData(
        scenario,
        selectedMetric,
        currentProject.projectObj.id,
        effectiveDateRange,
        true
      ),
  });

  const { data: targetsData } = useQuery({
    queryKey: [
      "mixResolvedTargets",
      currentProject?.projectObj?.id,
      scenario?.id,
    ],
    queryFn: () =>
      getMixResolvedTargets(
        apolloClient,
        localStorage.getItem("defaultTenantID"),
        currentProject.projectObj.id,
        scenario.id
      ),
    enabled: !!currentProject?.projectObj?.id && !!scenario?.id,
  });

  // Define formatMixData function first
  const formatMixData = (data, metricLine) => {
    const formattedData = Array.from(
      new Set(data?.source?.data.map((d) => d.timestamp))
    ).map((d) => {
      const formattedElement = {
        date: moment(d).utc().format("MMMM DD, YYYY"),
        timestamp: d,
      };

      data?.source?.data
        .filter((e) => e.timestamp === d)
        .forEach((e) => {
          formattedElement[e.mmm_channel || e.market] = e.spend;
        });

      // metrics represented as a line
      data?.target?.data
        .filter((e) => e.timestamp === d)
        .forEach((e) => {
          formattedElement[metricLine] = e[metricLine];
        });

      return formattedElement;
    });
    return formattedData;
  };

  // Define metricLine and barChartData after queries
  const metricLine = isUsingOnMix
    ? data?.target?.annotations?.metrics?.k?.friendlyName
    : channelData?.target?.annotations?.metrics?.k?.friendlyName;

  const barChartData = useMemo(() => {
    return isUsingOnMix
      ? !isLoading
        ? formatMixData(data, metricLine)
        : []
      : formatMixData(channelData, metricLine);
  }, [isUsingOnMix, isLoading, data, channelData, metricLine]);

  // Format dates for display in the chart and handle clipping
  const getClippedDateRange = useCallback(() => {
    if (!barChartData || barChartData.length === 0) {
      return null;
    }

    const selectedStart = moment(selectedDateRange.from);
    const selectedEnd = moment(selectedDateRange.to);

    // Find the closest chart data points that match or bound the selected dates
    const startPoint = barChartData.find((point) =>
      moment(point.date, "MMMM DD, YYYY").isSameOrAfter(selectedStart, "day")
    );

    const endPoint = [...barChartData]
      .reverse()
      .find((point) =>
        moment(point.date, "MMMM DD, YYYY").isSameOrBefore(selectedEnd, "day")
      );

    if (!startPoint || !endPoint) {
      return null;
    }

    return {
      from: startPoint.date,
      to: endPoint.date,
    };
  }, [barChartData, selectedDateRange]);

  useEffect(() => {
    // Reset selected metric when scenario changes
    if (scenario?.id) {
      setSelectedMetric(null);
    }
  }, [scenario?.id, setSelectedMetric]);

  useEffect(() => {
    if (targetsData?.length > 0 && !selectedMetric) {
      setSelectedMetric(targetsData[0].id);
    }
  }, [targetsData, selectedMetric, setSelectedMetric]);

  useEffect(() => {
    if (data?.insights_query) {
      fetchInsights(
        data,
        currentProject.projectObj.slug,
        currentProject.projectObj.id,
        currentProject.projectObj.insightsConfig,
        window.location.pathname.includes("/mix") ? "mix" : undefined
      ).then((data) => {
        if (data) {
          setState((old) => ({
            ...old,
            insights: data.map((insight) => ({
              ...insight,
              date: moment(insight.date).utc().format("MMMM DD, YYYY"),
            })),
          }));
        }
      });
    }
  }, [data, currentProject]);

  /**
   * Now all rows contains all attributes, so, we need to set all attributes in order to export it
   * properly into the csv;
   * @param {*} data
   */
  const setAllAttributesIntoData = (data) => {
    const allKeys = data.reduce((prior, current) => {
      const currentKeys = Object.keys(current);
      if (prior.length > currentKeys.length) {
        return prior;
      } else {
        return currentKeys;
      }
    }, []);

    data.forEach((d) => {
      allKeys.forEach((key) => {
        d[key] = d[key] || null;
      });
    });
    return data;
  };

  useEffect(() => {
    const metricLine =
      channelData?.target?.annotations?.metrics?.k?.friendlyName;

    const barChartData = formatMixData(channelData, metricLine);

    if (getExportData) {
      getExportData(setAllAttributesIntoData(barChartData));
    }
  }, [channelData, getExportData]);

  const getSortedKeys = (_data) => {
    if (!_data) return [];
    if (!_data[0]) return [];
    const data = _data.filter((d) => d);
    const keys = getAllKeys(data, ["date", "timestamp"]);

    const len = data.length;

    return keys
      .map((key) => ({
        key: key,
        value: data.reduce((accum, curr) => accum + (curr[key] || 0), 0) / len,
      }))
      .sort((a, b) => b.value - a.value)
      .map((d) => d.key);
  };

  const handleClickOutside = (e) => {
    // close all tooltips when the user clicks outside the context
    if (
      !e.target.classList.contains("recharts-rectangle") &&
      // and the parent isn't a xray tooltip
      !findParentNodeByClass(e.target, "xray-tooltip-container")
    )
      closeAllBarchartTooltips();
  };

  useEffect(() => {
    window.addEventListener("resize", closeAllBarchartTooltips);
    window.addEventListener("click", handleClickOutside);
    setTimeout(() => {
      closeAllBarchartTooltips();
    }, 500);
    return () => {
      window.removeEventListener("resize", closeAllBarchartTooltips);
      window.removeEventListener("click", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onBarClick = (d, dataKey, data) => {
    // Get the sorted keys to ensure consistent color ordering
    const sortedKeys = getSortedKeys(data);

    d.extra = sortedKeys
      .map((k, j) => {
        // Get the index of this key in the sorted keys array for color consistency
        const keyIndex = sortedKeys.indexOf(k);
        return {
          key: k,
          value: d[k],
          color: MAIN_COLORS[customColorIndex(keyIndex)],
        };
      })
      .filter((o) => o.value !== undefined)
      .sort((a, b) => b.value - a.value);

    let show = false;
    if (!(state.tooltip.show && d.date === state.tooltip.date)) {
      show = true;
      // close all tooltips from this and other widgets as well
      closeAllBarchartTooltips();
    }

    setState((oldState) => ({
      ...oldState,
      tooltip: { ...d, show, dataKey },
    }));
  };

  const closeTooltip = () => {
    setState((s) => ({
      ...s,
      tooltip: { show: false },
    }));
  };

  const renderBars = (data, metricLines) => {
    let bars = [];
    const sortedKeys = getSortedKeys(data);
    const isFixedBarWidth = data.length <= 7;

    // First render all bars
    [...new Set(sortedKeys)]
      .filter((bar) => bar !== metricLines)
      .forEach((bar, i) => {
        // Get the index of this bar in the sorted keys array for color consistency
        const barIndex = sortedKeys.indexOf(bar);

        let stack = "stack";
        bars.push(
          <Bar
            key={i}
            dataKey={bar}
            stackId={stack}
            yAxisId="left"
            barSize={isFixedBarWidth ? 35 : null}
            fill={MAIN_COLORS[customColorIndex(barIndex)]}
            isAnimationActive={false}
            cursor="pointer"
            onClick={(d) => onBarClick(d, bar, data)}
            background={
              i === 0 ? (
                <RenderBackground
                  insights={state.insights}
                  showIndicator={!state.tooltip.show}
                  node={refNode}
                  i={i}
                />
              ) : null
            }
          >
            {data.map(
              (entry, index) =>
                entry && (
                  <Cell
                    key={`cell-${bar}-${index}`}
                    fillOpacity={entry[bar] < 0 ? 0.5 : 1}
                  ></Cell>
                )
            )}
          </Bar>
        );
      });

    // Add the line last
    if (metricLines && sortedKeys.includes(metricLines)) {
      bars.push(
        <Line
          key="metric-line"
          yAxisId="right"
          type="monotone"
          dataKey={metricLines}
          stroke="#868E96"
          dot={false}
          onClick={(d) => onBarClick(d, metricLines, data)}
        />
      );
    }

    return bars;
  };

  const legendElement = (data) => {
    const sortedKeys = getSortedKeys(barChartData);

    return data.map((entry, i) => {
      // Get the index of this entry in the sorted keys array for color consistency
      const entryIndex = sortedKeys.indexOf(entry);
      const colorIndex = customColorIndex(entryIndex !== -1 ? entryIndex : i);

      return (
        <span key={i} className="legend mt-2 mt-md-0">
          <span
            className="legend__icon"
            style={{ background: MAIN_COLORS[colorIndex] }}
          ></span>
          <span
            className="legend__text"
            style={{ color: MAIN_COLORS[colorIndex] }}
          >
            {entry.replaceAll("_", " ")}
          </span>
        </span>
      );
    });
  };

  const renderLegends = (data) => {
    // Make sure we have data before proceeding
    if (!data || data.length === 0) {
      return null;
    }

    const sortedKeys = getSortedKeys(data);

    // Filter out timestamp and metricLine from the sorted keys
    const filteredKeys = sortedKeys.filter(
      (key) => key !== "timestamp" && key !== metricLine
    );

    // Use filtered keys as the legend items
    return legendElement(filteredKeys);
  };

  // Add a useEffect to monitor the date ranges
  useEffect(() => {
    getClippedDateRange();
  }, [selectedDateRange, getClippedDateRange]);

  // Create a unique key for the chart based on the selected date range and timeframe mode
  const chartKey = `${selectedDateRange.from}-${selectedDateRange.to}-${timeframeMode}`;

  const { x, y, width, show } = state.tooltip;
  const tooltipContainerStyle = {
    top: y - 20 + "px",
    left:
      x + width + 220 < width
        ? x + width + 15 + "px"
        : x - getXrayTooltipPosition(divPosition.current, state.tooltip) + "px",
    opacity: show ? 1.0 : 0.0,
    visibility: show ? "visible" : "hidden",
  };

  const getChartExportData = () => {
    const chartData = isUsingOnMix
      ? !isLoading
        ? formatMixData(data, metricLine)
        : []
      : formatMixData(channelData, metricLine);

    return {
      data: chartData.map((d) => ({
        ...d,
        date: moment(d.timestamp).utc().format("YYYY-MM-DD"),
      })),
      opts: {
        type: "chart",
        title: "Mix Channel Analysis",
        date: {
          name: "date",
          title: "Date",
          defined: true,
          format: "YYYY-MM-DD",
          viewFormat: "YYYY-MM-DD",
          no_date: true,
        },
        series: {
          title: "Channel",
          format: "currency",
        },
        metric: {
          title: "Metric",
          value: metricLine,
        },
        value: {
          title: "Value",
          format: "currency",
        },
        exclude: ["timestamp"],
      },
    };
  };

  // Calculate the y-axis range for the reference area
  // eslint-disable-next-line no-unused-vars
  const getYAxisRange = (data) => {
    if (!data || data.length === 0) return { min: 0, max: 1 };

    let min = Infinity;
    let max = -Infinity;

    data.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (typeof item[key] === "number") {
          min = Math.min(min, item[key]);
          max = Math.max(max, item[key]);
        }
      });
    });

    // Add some padding to ensure full coverage
    const padding = (max - min) * 0.1;
    return {
      min: min - padding,
      max: max + padding,
    };
  };

  // Create a custom setTimeframeMode that also updates referenceYear when needed
  const handleTimeframeModeChange = (mode) => {
    if (mode === "lastYear") {
      // When switching to lastYear, always use the current year as reference
      setReferenceYear(moment().year());
    }
    setTimeframeMode(mode);
  };

  return (
    <div className="row mix-detail-card mix-historical-analysis">
      <div className="col-12">
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center mb-4">
            <h6 className="pt-3 d-inline mr-3">Channel Analysis:</h6>
            {!isUsingOnMix ? null : (
              <div className="d-flex align-items-center">
                <TargetDropdown
                  selectedMetric={selectedMetric}
                  setSelectedMetric={setSelectedMetric}
                  scenario={scenario}
                />
                <TimeframeToggle
                  timeframeMode={timeframeMode}
                  setTimeframeMode={handleTimeframeModeChange}
                />
              </div>
            )}
          </div>
          <div className="d-flex align-items-center">
            <CSVExport
              childRef={() => ({
                ...chartRef.current,
                getExportData: getChartExportData,
              })}
            />
          </div>
        </div>
        {/* Drop Down  with subscribers video views and search */}

        <div className="col-12 mix-widget">
          {isUsingOnMix && isLoading ? (
            <Loader minHeight={370} col={"col-12"} size="circle-scale--sm" />
          ) : (
            <>
              <div ref={divLegendRef}>{renderLegends(barChartData)}</div>
              <div ref={chartRef}>
                <ResponsiveContainer height={370} width="100%">
                  <ComposedChart
                    key={chartKey}
                    data={barChartData}
                    stackOffset="sign"
                    margin={{ top: 10, right: -45, bottom: 0, left: -45 }}
                  >
                    <CartesianGrid
                      stroke="#FEE5DE"
                      strokeDasharray="3 3"
                      vertical={false}
                    />

                    <ReferenceArea
                      key={`reference-${chartKey}`}
                      x1={getClippedDateRange()?.from || barChartData[0]?.date}
                      x2={getClippedDateRange()?.to || barChartData[0]?.date}
                      yAxisId="left"
                      fill="#FFE5E5"
                      fillOpacity={0.8}
                      ifOverflow="hidden"
                      z={-1}
                    />

                    {state?.insights?.map((o, index) => (
                      <ReferenceDot
                        x={o.date}
                        y={0}
                        key={"mix-channel-analysis" + index}
                        yAxisId="left"
                        ifOverflow="extendDomain"
                        shape={
                          <InsightsTickIndicator
                            insights={state.insights}
                            date={o.date}
                            node={refNode}
                          />
                        }
                      />
                    ))}

                    {!state.tooltip.show && (
                      <Tooltip
                        content={
                          <StandardTooltip
                            render={({ data }) => {
                              const payload = data(barChartData);
                              const sortedKeys = getSortedKeys(barChartData);

                              if (payload !== null && payload.length > 0) {
                                const tooltip_data = payload[0].payload;
                                return (
                                  <div className="standard-tooltip">
                                    <header className="standard-tooltip__header">
                                      <span className="standard-tooltip__key-header">
                                        {tooltip_data.date}
                                      </span>
                                    </header>
                                    <main className="standard-tooltip__main">
                                      {payload
                                        .filter(
                                          (item) =>
                                            item.dataKey !== "timestamp" &&
                                            item.dataKey !== metricLine
                                        )
                                        .sort((a, b) => b.value - a.value)
                                        .map((item) => {
                                          // Get the index of this item in the sorted keys array for color consistency
                                          const itemIndex = sortedKeys.indexOf(
                                            item.dataKey
                                          );
                                          return (
                                            <div
                                              className="standard-tooltip__item"
                                              key={item.dataKey}
                                            >
                                              <span
                                                className="standard-tooltip__icon"
                                                style={{
                                                  background:
                                                    MAIN_COLORS[
                                                      customColorIndex(
                                                        itemIndex
                                                      )
                                                    ],
                                                }}
                                              />
                                              <span
                                                className={clsx(
                                                  "standard-tooltip__key",
                                                  "text-capitalize"
                                                )}
                                                style={{
                                                  color:
                                                    MAIN_COLORS[
                                                      customColorIndex(
                                                        itemIndex
                                                      )
                                                    ],
                                                }}
                                              >
                                                {item.dataKey.replaceAll(
                                                  "_",
                                                  " "
                                                )}
                                              </span>
                                              <span className="standard-tooltip__value">
                                                {selectFormat(
                                                  "currency",
                                                  item.value || 0
                                                )}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      <div className="standard-tooltip__dilimiter1" />

                                      <div className="standard-tooltip__item">
                                        <span
                                          className={clsx(
                                            "standard-tooltip__key",
                                            "text-capitalize",
                                            "total"
                                          )}
                                        >
                                          Total{" "}
                                        </span>
                                        <span className="standard-tooltip__value">
                                          {selectFormat(
                                            "currency",
                                            payload
                                              .filter(
                                                (item) =>
                                                  item.dataKey !==
                                                    "timestamp" &&
                                                  item.dataKey !== metricLine
                                              )
                                              .reduce(
                                                (sum, item) =>
                                                  sum + (item.value || 0),
                                                0
                                              )
                                          )}
                                        </span>
                                      </div>

                                      <div className="standard-tooltip__dilimiter1" />

                                      <div className="standard-tooltip__item">
                                        <span
                                          className={clsx(
                                            "standard-tooltip__key",
                                            "text-capitalize",
                                            "total"
                                          )}
                                        >
                                          {metricLine || ""}
                                        </span>
                                        <span className="standard-tooltip__value">
                                          {selectFormat(
                                            "number",
                                            payload?.find(
                                              (e) => e?.name === metricLine
                                            )?.value || 0
                                          )}
                                        </span>
                                      </div>

                                      {state.insights?.length > 0 && (
                                        <InsightsWithinTooltip
                                          data={state.insights}
                                          tooltipDate={tooltip_data?.date}
                                        />
                                      )}
                                    </main>
                                  </div>
                                );
                              }
                            }}
                          />
                        }
                      />
                    )}
                    {renderBars(barChartData, metricLine)}

                    <XAxis
                      dataKey="date"
                      tickLine={false}
                      axisLine={false}
                      scale="auto"
                      height={60}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      yAxisId="left"
                      tickFormatter={(d) =>
                        selectFormat("currency", d, "$0,00")
                      }
                      tick={<CustomizedYAxisTickLeftCurrency />}
                    />
                    <YAxis
                      yAxisId="right"
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(d) => selectFormat("number", d)}
                      tick={<CustomizedYAxisTickRight />}
                      orientation="right"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
                <div
                  className="xray-tooltip-container"
                  style={tooltipContainerStyle}
                >
                  <XRayTooltip
                    data={state.tooltip}
                    close={closeTooltip}
                    distinctTotals={[metricLine]}
                    insights={state.insights}
                  />
                </div>
                <div
                  className="featured-insight-tooltip"
                  ref={refNode}
                  style={{ top: height + "px" }}
                >
                  {state.insights?.length > 0 && !state.tooltip.show && (
                    <RenderTooltipInsights data={state.insights} />
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <div
        className="featured-insight-tooltip"
        ref={refNode}
        style={{ top: "90px" }}
      >
        {state.insights?.length > 0 && !state.tooltip.show && (
          <RenderTooltipInsights data={state.insights} />
        )}
      </div>
    </div>
  );
};

export default MixChannelAnalysis;
